import type { ReactNode } from "react";
import { DateTime } from "luxon";
import { useFormContext } from "react-hook-form";
import { useFacilitiesByUserId } from "@/modules/facility/facilityHooks";
import { getUserIdFromToken } from "@/modules/user/userHooks";
import {
  Clubhouse_LeagueFacilityScope,
  Clubhouse_LeagueType,
  Clubhouse_LeagueWizardScheduleMode,
} from "../../enums";
import { leagueTypeLabel, scoringLabel } from "../helpers";
import type { LeagueFormValues } from "../leagueFormSchema";

const EM_DASH = "—";

/** "Sep 14 – Dec 1, 2026" — the year is stated once, on the end date. */
function seasonDates(start: string, end: string): string {
  const s = start ? DateTime.fromISO(start) : undefined;
  const e = end ? DateTime.fromISO(end) : undefined;
  if (!s?.isValid || !e?.isValid) return EM_DASH;
  return `${s.toFormat("MMM d")} – ${e.toFormat("MMM d, yyyy")}`;
}

function Card({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="overflow-hidden rounded-xl border border-border bg-card">
      <header className="border-b border-border bg-muted/60 px-4 py-2.5">
        <h3 className="poppins m-0 text-sm font-bold tracking-tight text-text-primary">
          {title}
        </h3>
      </header>
      <div>{children}</div>
    </section>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-border px-4 py-2.5 last:border-0">
      <span className="mulish text-[13px] font-semibold text-text-muted">
        {label}
      </span>
      <span className="mulish text-right text-[13px] font-bold text-text-primary">
        {value}
      </span>
    </div>
  );
}

export interface LeagueReviewStepProps {
  /**
   * Whether the operator was asked where the league can be played. A
   * single-facility operator never sees that step, so there is no choice of
   * theirs to show back.
   */
  showLocation: boolean;
}

/**
 * A record of what the operator chose, and only that: no setting Guided applied
 * on their behalf (round settings, team size, tees) and no participant cap —
 * Guided no longer asks for one.
 */
export function LeagueReviewStep({ showLocation }: LeagueReviewStepProps) {
  const { getValues } = useFormContext<LeagueFormValues>();
  const values = getValues();
  const [userFacilities] = useFacilitiesByUserId(getUserIdFromToken());

  const leagueType = values.wizard?.leagueType;
  const scoring = values.wizard?.leagueScoringType;
  const isScramble = leagueType === Clubhouse_LeagueType.SCRAMBLE;

  const selectedIds = values.participatingClubhouseFacilityIds ?? [];
  const facilities =
    values.facilityScope === Clubhouse_LeagueFacilityScope.ANY_OF_MY_FACILITIES
      ? userFacilities
          .filter(
            (f) =>
              f.clubhouseFacilityId != null &&
              selectedIds.includes(f.clubhouseFacilityId),
          )
          .map((f) => f.name)
          .join(", ")
      : "This facility only";

  return (
    <div className="flex flex-col gap-3.5">
      <Card title="League Details">
        <Row label="League Name" value={values.name || EM_DASH} />
        <Row label="Description" value={values.description || EM_DASH} />
      </Card>

      <Card title="Format">
        <Row
          label="League Format"
          value={leagueType ? leagueTypeLabel[leagueType] : EM_DASH}
        />
        {/* Scramble is not asked for a scoring format, so it has none to show. */}
        {!isScramble && (
          <Row
            label="Scoring Format"
            value={scoring ? scoringLabel[scoring] : EM_DASH}
          />
        )}
      </Card>

      <Card title="Schedule">
        <Row
          label="Season Dates"
          value={seasonDates(values.leagueStartDate, values.leagueEndDate)}
        />
        <Row
          label="Schedule"
          value={
            values.wizard?.scheduleMode ===
            Clubhouse_LeagueWizardScheduleMode.SeasonFlex
              ? "Flex"
              : "Fixed"
          }
        />
      </Card>

      {showLocation && (
        <Card title="Location">
          <Row label="Facilities" value={facilities || EM_DASH} />
        </Card>
      )}
    </div>
  );
}
